import { BadgeData, BadgeDesign } from '../../../../shared/user/badgeDesign';

export type PrinterConfig = Record<string, unknown>;

export enum PrinterManagerStatus {
  Ready = 'ready',
  Scanning = 'scanning',
  Error = 'error',
}

export enum PrinterStatus {
  Connected = 'connected',
  Disconnected = 'disconnected',
}

/** The interface type for communicating with a printer */
export enum PrinterType {
  /** Use the operating system print APIs and dialog. */
  OS = 'os',
  /** Use the WebZLP library to communicate with label printers. */
  WebLabel = 'weblabel',
  /** Use the Web-ReceiptLine-Printer library to communicate with receipt printers. */
  WebReceipt = 'webreceipt',
}

interface PrinterBase {
  readonly name: string;
  readonly serial: string;
  readonly type: PrinterType;
  readonly canConfigure: boolean;
  readonly uid: string;

  testPrint(): Promise<void>;
}

export interface ReceiptPrinterBase extends PrinterBase {
  /** Print a receipt document. */
  print(receiptline: string): Promise<void>;

  /** Kick open a cash drawer, assuming one is connected. */
  drawerKick(): Promise<void>;
}

export interface BadgePrinterBase extends PrinterBase {
  /** Print a badge */
  print(badgeData: BadgeData, badgeDesign: BadgeDesign, reason?: string): Promise<void>;
}

export interface PrinterInfoBase {
  readonly name: string;
  readonly serial: string;
  readonly type: PrinterType;
  readonly config: PrinterConfig;
  readonly uid: string;
}

/** Get a unique ID for a given printer. */
export function getPrinterUid(type: PrinterType, serial: string): string {
  return `${type}:${serial}`;
}

/** Decode a unique ID for a printer. */
export function parsePrinterUid(str?: string):
  | {
      type: PrinterType;
      serial: string;
    }
  | undefined {
  if (str === undefined) {
    return;
  }

  const [typeRaw, serial] = str.split(':') as [string, string?];
  if (serial === undefined || serial.length === 0) {
    return;
  }

  let type: PrinterType | undefined;
  switch (typeRaw.toUpperCase()) {
    case 'OS': {
      type = PrinterType.OS;
      break;
    }

    case 'WEBLABEL': {
      type = PrinterType.WebLabel;
      break;
    }

    case 'WEBRECEIPT': {
      type = PrinterType.WebReceipt;
      break;
    }
  }

  if (type === undefined) {
    return;
  }

  return { type, serial };
}
